import { parsePhoneNumber } from 'awesome-phonenumber';
import {
  PHONE_FAKE_PATTERN_TAIL_LENGTH,
  PHONE_MAX_DIGITS,
  PHONE_MIN_DIGITS,
  PHONE_VALIDATION_MESSAGES,
} from '~/constants/phone';

export type PhoneValidationError = keyof typeof PHONE_VALIDATION_MESSAGES;

export type PhoneValidationResult =
  | { valid: true; e164: string; nationalNumber: string; regionCode: string | null }
  | { valid: false; error: PhoneValidationError; message: string };

// Keep only the digits of an international number and drop a leading "00"
// prefix. Tolerates the dirty shapes we store/receive: spaces, dashes, and the
// double "++" prefix ("++31 64…").
export const toPhoneDigits = (raw: unknown): string => {
  const digits = String(raw ?? '').replace(/\D/g, '');
  return digits.startsWith('00') ? digits.slice(2) : digits;
};

const isRepeatedDigit = (digits: string): boolean => /^(\d)\1+$/.test(digits);

const isSequential = (digits: string, step: 1 | -1): boolean => {
  for (let i = 1; i < digits.length; i += 1) {
    if ((Number(digits[i - 1]) + step + 10) % 10 !== Number(digits[i])) return false;
  }
  return true;
};

// "0000000", "1234567", "9876543" as the subscriber part — the usual
// throwaway numbers typed into signup forms.
export const isFakePhonePattern = (nationalNumber: string): boolean => {
  const tail = nationalNumber.slice(-PHONE_FAKE_PATTERN_TAIL_LENGTH);
  if (tail.length < PHONE_FAKE_PATTERN_TAIL_LENGTH) return isRepeatedDigit(nationalNumber);
  return isRepeatedDigit(tail) || isSequential(tail, 1) || isSequential(tail, -1);
};

const fail = (error: PhoneValidationError): PhoneValidationResult => ({
  valid: false,
  error,
  message: PHONE_VALIDATION_MESSAGES[error],
});

// Validates an international phone number (must include the country code) and
// returns its canonical E.164 form. Shared by the client inputs and the server
// endpoints so both reject the same numbers with the same message.
export const validatePhone = (raw: unknown): PhoneValidationResult => {
  const digits = toPhoneDigits(raw);
  if (!digits) return fail('missing');
  if (digits.length < PHONE_MIN_DIGITS) return fail('tooShort');
  if (digits.length > PHONE_MAX_DIGITS) return fail('tooLong');

  const parsed = parsePhoneNumber(`+${digits}`);
  const nationalNumber = parsed.number?.significant ?? digits;
  if (isFakePhonePattern(nationalNumber)) return fail('fakePattern');
  if (!parsed.valid || !parsed.number?.e164) {
    if (parsed.possibility === 'too-short') return fail('tooShort');
    if (parsed.possibility === 'too-long') return fail('tooLong');
    return fail('invalid');
  }

  return { valid: true, e164: parsed.number.e164, nationalNumber, regionCode: parsed.regionCode ?? null };
};
